import { createContext, useContext, useState, useMemo, cloneElement, isValidElement } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import classNames from "classnames";
import { Button } from "app/components/ui/Button/Button";
import { ChevronLeftIcon } from "app/components/icons/navigation/ChevronLeftIcon";
import useBlockScroll from "app/utils/hooks/useBlockScroll";
import "./Drawer.scss";

const composeHandlers = (userHandler, internalHandler) => {
	return e => {
		if (typeof userHandler === "function") {
			userHandler(e);
		}

		if (e?.defaultPrevented) {
			return;
		}

		if (typeof e.isPropagationStopped === "function" && e.isPropagationStopped()) {
			return;
		}

		if (typeof internalHandler === "function") {
			internalHandler(e);
		}
	};
};

const DrawerContext = createContext(null);

const useDrawerContext = () => {
	const ctx = useContext(DrawerContext);

	if (!ctx) {
		throw new Error("Drawer components must be used inside <Drawer.Root>.");
	}

	return ctx;
};

const DrawerRoot = ({
	children,
	defaultOpen = false,
	open: controlledOpen,
	onOpenChange,
	side = "right",
	maxSize,
	closeOnOverlayClick = true,
	ariaLabel = "Drawer panel",
	animationDuration = 200,
}) => {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const isControlled = typeof controlledOpen === "boolean";
	const open = isControlled ? controlledOpen : uncontrolledOpen;

	useBlockScroll(open);

	const setOpen = next => {
		if (!isControlled) {
			setUncontrolledOpen(next);
		}

		if (onOpenChange) {
			onOpenChange(next);
		}
	};

	const value = useMemo(
		() => ({
			open,
			setOpen,
			side,
			maxSize,
			closeOnOverlayClick,
			ariaLabel,
			animationDuration,
		}),
		[open, side, maxSize, closeOnOverlayClick, ariaLabel, animationDuration]
	);

	return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};

DrawerRoot.propTypes = {
	children: PropTypes.node.isRequired,
	defaultOpen: PropTypes.bool,
	open: PropTypes.bool,
	onOpenChange: PropTypes.func,
	side: PropTypes.oneOf(["left", "right", "top", "bottom"]),
	maxSize: PropTypes.number,
	closeOnOverlayClick: PropTypes.bool,
	ariaLabel: PropTypes.string,
	animationDuration: PropTypes.number,
};

const DrawerTrigger = ({ children }) => {
	const { open, setOpen } = useDrawerContext();

	if (!isValidElement(children)) {
		return null;
	}

	const handler = composeHandlers(children.props.onClick, () => setOpen(true));

	return cloneElement(children, {
		...children.props,
		"aria-expanded": open,
		onClick: handler,
	});
};

DrawerTrigger.propTypes = {
	children: PropTypes.element.isRequired,
};

const DrawerContent = ({ children, className, overlayClassName, ...rest }) => {
	const { open, setOpen, side, maxSize, closeOnOverlayClick, ariaLabel, animationDuration } =
		useDrawerContext();

	const contentSize =
		side === "left" || side === "right"
			? {
					width: "100dvw",
					maxWidth: maxSize ? `${maxSize}px` : "100dvw",
				}
			: {
					height: "100dvh",
					maxHeight: maxSize ? `${maxSize}px` : "100dvh",
				};

	return (
		<Modal
			isOpen={open}
			onRequestClose={() => setOpen(false)}
			shouldCloseOnOverlayClick={closeOnOverlayClick}
			contentLabel={ariaLabel}
			closeTimeoutMS={animationDuration}
			className={{
				base: classNames("drawer", `drawer--${side}`, className),
				afterOpen: "drawer--after-open",
				beforeClose: "drawer--before-close",
			}}
			overlayClassName={{
				base: classNames("drawer__overlay", overlayClassName),
				afterOpen: "drawer__overlay--after-open",
				beforeClose: "drawer__overlay--before-close",
			}}
			style={{
				overlay: { "--drawer-animation-duration": `${animationDuration}ms` },
				content: { ...contentSize },
			}}
			{...rest}
		>
			{children}
		</Modal>
	);
};

DrawerContent.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	overlayClassName: PropTypes.string,
};

const DrawerClose = ({ asChild = false, children, className, onClick, ...rest }) => {
	const { setOpen } = useDrawerContext();

	const handleClick = () => {
		setOpen(false);

		if (typeof onClick === "function") {
			onClick();
		}
	};

	if (asChild && isValidElement(children)) {
		const handler = composeHandlers(children.props.onClick, handleClick);
		return cloneElement(children, {
			...children.props,
			onClick: handler,
		});
	}

	return (
		<Button
			variant="borderless"
			size="icon-lg"
			startIcon={<ChevronLeftIcon />}
			onClick={handleClick}
			className={classNames("drawer-close-btn", className)}
			{...rest}
		/>
	);
};

DrawerClose.propTypes = {
	asChild: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string,
	onClick: PropTypes.func,
};

const DrawerHeader = ({
	asChild = false,
	as: Tag = "header",
	showClose = true,
	onClose,
	children,
	className,
}) => {
	if (asChild && isValidElement(children)) {
		return cloneElement(children, {
			className: classNames("drawer-header", children.props.className, className),
		});
	}

	return (
		<Tag className={classNames("drawer-header", className)}>
			{showClose && <DrawerClose onClick={onClose} />}
			{children}
		</Tag>
	);
};

DrawerHeader.propTypes = {
	asChild: PropTypes.bool,
	as: PropTypes.elementType,
	showClose: PropTypes.bool,
	onClose: PropTypes.func,
	children: PropTypes.node,
	className: PropTypes.string,
};

const DrawerBody = ({ asChild = false, as: Tag = "div", children, className }) => {
	if (asChild && isValidElement(children)) {
		return cloneElement(children, {
			className: classNames("drawer-body", children.props.className, className),
		});
	}

	return <Tag className={classNames("drawer-body", className)}>{children}</Tag>;
};

DrawerBody.propTypes = {
	asChild: PropTypes.bool,
	as: PropTypes.elementType,
	children: PropTypes.node,
	className: PropTypes.string,
};

const DrawerFooter = ({ asChild = false, as: Tag = "footer", children, className }) => {
	if (asChild && isValidElement(children)) {
		return cloneElement(children, {
			className: classNames("drawer-footer", children.props.className, className),
		});
	}
	return <Tag className={classNames("drawer-footer", className)}>{children}</Tag>;
};

DrawerFooter.propTypes = {
	asChild: PropTypes.bool,
	as: PropTypes.elementType,
	children: PropTypes.node,
	className: PropTypes.string,
};

export const Drawer = {
	Root: DrawerRoot,
	Trigger: DrawerTrigger,
	Content: DrawerContent,
	Close: DrawerClose,
	Header: DrawerHeader,
	Body: DrawerBody,
	Footer: DrawerFooter,
};
